import { proxy } from 'valtio'
import { proxySet } from 'valtio/utils'

import type { Floor } from '../../firebase/model/floor-model'
import { roomState } from '../../webrtc/webRTCRoom'

import type { SelectorKey } from './protocol'

// A resolved selection references actual participant ids (not speaker indexes)
// so macros survive speaker reordering. Participants that have left are
// dropped gracefully when the selection is applied.
type Selection = {
  speakerIds: number[]
  screenIds: number[]
  tray: boolean
}

// A macro either stores an explicit selection (made while recording) or a
// snapshot of the floor state (recording exited without touching the
// selection) — the latter keeps custom mixes as references, not approximations.
type Macro =
  | { type: 'selection'; selection: Selection }
  | {
      type: 'floor'
      floor: Floor
      panel?: { participantIds: number[]; screen: number | 'tray' | undefined }
    }

type MidiState = {
  enabled: boolean
  lights: Record<string, boolean>
  pressedSelectors: Set<SelectorKey>
  pressedMacros: Set<number> // macro slot indexes (0-based)
  recording: boolean
  recordedSelection: Set<SelectorKey>
  // whether any selector was toggled while recording — decides between
  // storing the selection or the current floor state
  recordingTouched: boolean
  macros: Partial<Record<number, Macro>>
}

const midiState = proxy<MidiState>({
  enabled: false,
  recording: false,
  lights: {},
  pressedSelectors: proxySet<SelectorKey>(),
  pressedMacros: proxySet<number>(),
  recordedSelection: proxySet<SelectorKey>(),
  recordingTouched: false,
  macros: {},
})

// ====== Macro persistence ====== //
// Future work: persistence is localStorage-only for now, so macros are
// per-browser and never shared between producers. Once multi-user semantics
// are figured out, move this to a firebase node (e.g. `midiMacros`) mirroring
// the pattern in useMixes.ts, so macros sync across clients and reloads.

const MACROS_STORAGE_KEY = 'room-midi-macros'

const loadMacros = () => {
  try {
    const raw = localStorage.getItem(MACROS_STORAGE_KEY)
    if (raw) midiState.macros = JSON.parse(raw) as MidiState['macros']
  } catch {
    // corrupted storage — start fresh
    midiState.macros = {}
  }
}

const saveMacros = () => {
  try {
    localStorage.setItem(MACROS_STORAGE_KEY, JSON.stringify(midiState.macros))
  } catch {
    // storage full/unavailable — macros just won't persist
  }
}

// ====== Participants mapping ====== //
// Selector buttons always follow the *current* speaker order (same ordering
// as useSortedHumanParticipants). Macros store participant ids instead, so
// they keep pointing at the same people if the order changes.

const getSortedHumanParticipants = () =>
  [...roomState.participants]
    .filter((p) => p.role !== 'headless')
    .sort((a, b) => a.joinedAt - b.joinedAt)

/** speaker index (1-based) -> participant id */
const speakerIdxToParticipantId = (idx: number): number | undefined =>
  getSortedHumanParticipants()[idx - 1]?.id

/** participant id -> speaker index (1-based) */
const participantIdToSpeakerIdx = (id: number): number | undefined => {
  const idx = getSortedHumanParticipants().findIndex((p) => p.id === id)
  return idx === -1 ? undefined : idx + 1
}

// ====== Media availability ====== //
// Selections should only ever reference media that is currently in the room:
// a speaker is available when the participant is present, a screen only when
// that participant is actively screensharing.

const isSpeakerAvailable = (id: number) =>
  getSortedHumanParticipants().some((p) => p.id === id)

const isScreenAvailable = (id: number) =>
  getSortedHumanParticipants().some(
    (p) => p.id === id && p.tracks.screen?.isEnabled
  )

const isSelectorAvailable = (selector: SelectorKey): boolean => {
  if (selector === 'tray') return true
  if (selector.startsWith('speaker-')) {
    return speakerIdxToParticipantId(Number(selector.slice(8))) !== undefined
  }
  if (selector.startsWith('screen-')) {
    const id = speakerIdxToParticipantId(Number(selector.slice(7)))
    return id !== undefined && isScreenAvailable(id)
  }
  return false
}

export {
  midiState,
  loadMacros,
  saveMacros,
  getSortedHumanParticipants,
  speakerIdxToParticipantId,
  participantIdToSpeakerIdx,
  isSpeakerAvailable,
  isScreenAvailable,
  isSelectorAvailable,
  type Selection,
  type Macro,
  type MidiState,
}
